/**
 * auth-context.tsx — AuthContext, AuthProvider, useAuth hook
 *
 * Purpose:    Single source of auth state for the ɳSentry app zone. Calls
 *             getUser once on mount. Eliminates per-component race conditions.
 * Inputs:     auth.getUser() from ./api
 * Outputs:    user, loading, error, refetch, signOut
 * Constraints: React 19; must be mounted inside BrowserRouter
 * SOT:        nsentry SaaS auth zone — ported from web/ntask
 */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { auth, type AuthUser } from './api'
import { AUTH_EXPIRED_EVENT } from './saas-client'

interface AuthState {
  user: AuthUser | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    setError(null)
    const result = await auth.getUser()
    if (result.error || !result.data) {
      setUser(null)
      setError(result.error?.message ?? 'Not authenticated')
    } else {
      setUser(result.data)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void fetchUser()
  }, [fetchUser])

  // A 401 from any authed /v1 call means the session is gone — clear the
  // user so RequireAuth redirects to /app/login (spec: 401 → re-login).
  useEffect(() => {
    function handleExpired() {
      setUser(null)
      setError('Session expired — please sign in again')
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired)
  }, [])

  const signOut = useCallback(async () => {
    await auth.signOut()
    setUser(null)
    setError(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, error, refetch: fetchUser, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
